import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";

import { APIEndpoints } from "@/apis/endpoints";
import StickyHeader from "@/components/StickyHeader";
import { ActivateBrowserDefender } from "@/pages/browser-defender/components/ActivateBrowserDefender";
import DataPerCompany from "@/pages/browser-defender/extensionGovernance/DataPerCompany";
import { useUserContext } from "@/utils/contexts/UserContext";

export function ExtensionGovernance() {
  const { t } = useTranslation();

  const [data, setData] = useState({
    values: [],
    filteredValues: [],
    loading: true,
  });

  const [sorting, setSorting] = useState({
    column: "warning_count",
    direction: -1,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedExtension, setSelectedExtension] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [activeFeature, setActiveFeature] = useState(true);
  const [error, setError] = useState(false);

  const onSearchTermChange = (e) => {
    if (e.keyCode !== 13) {
      return;
    }
    const value = e.target.value;
    setSearchTerm(value);
  };

  const { getAccessTokenSilently } = useAuth0();
  const info_user = useUserContext();

  const fetchData = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const url = `${APIEndpoints.BROWSER}/logs/browser_extensions_per_company`;
      let resource;

      try {
        resource = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (e) {
        if (e.response.status === 400) {
          setData({
            values: [],
            filteredValues: [],
            loading: false,
          });
          setActiveFeature(false);
          return;
        }
      }

      resource.data = resource.data.map((extension) => ({
        ...extension,
        warning_count: extension.risk_score_details.length,
        active_users: extension.users.filter(
          (obj) => obj.uninstalled === false && obj.enabled === true,
        ).length,
      }));

      setData({
        values: resource.data,
        filteredValues: resource.data,
        loading: false,
      });
    } catch (e) {
      Sentry.captureException(e);
      setError(true);
    }
  }, [getAccessTokenSilently]);

  const handleBlock = async () => {
    if (!selectedExtension) return;
    setActionLoading(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(
        `${APIEndpoints.BROWSER}/admin_actions/extensions/block`,
        { internal_id: selectedExtension.internal_id },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data.status === "success") {
        // Update local state to reflect the change
        setData((prevData) => ({
          ...prevData,
          values: prevData.values.map((ext) =>
            ext.internal_id === selectedExtension.internal_id
              ? { ...ext, blocked: true }
              : ext,
          ),
          filteredValues: prevData.filteredValues.map((ext) =>
            ext.internal_id === selectedExtension.internal_id
              ? { ...ext, blocked: true }
              : ext,
          ),
        }));
        setSelectedExtension(null);
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAllow = async () => {
    if (!selectedExtension) return;
    setActionLoading(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(
        `${APIEndpoints.BROWSER}/admin_actions/extensions/allow`,
        { internal_id: selectedExtension.internal_id },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data.status === "success") {
        setData((prevData) => ({
          ...prevData,
          values: prevData.values.map((ext) =>
            ext.internal_id === selectedExtension.internal_id
              ? { ...ext, blocked: false }
              : ext,
          ),
          filteredValues: prevData.filteredValues.map((ext) =>
            ext.internal_id === selectedExtension.internal_id
              ? { ...ext, blocked: false }
              : ext,
          ),
        }));
        setSelectedExtension(null);
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!info_user.email) {
      return;
    }

    fetchData();
  }, [fetchData, info_user]);

  useEffect(() => {
    const filterColumns = ["name"];
    let filteredValues = [];
    if (!searchTerm) {
      filteredValues = data.values;
    } else {
      filteredValues = data.values.filter(function (row) {
        return Object.keys(row).some(function (attribute) {
          if (!filterColumns.includes(attribute)) {
            return false;
          }

          if (!row[attribute]) {
            return false;
          }

          let value;

          try {
            value = row[attribute].toLowerCase();
          } catch {
            value = row[attribute];
          }

          return value.indexOf(searchTerm.toLowerCase()) > -1;
        });
      });
    }

    if (!sorting.column) {
      setSorting({ column: "warning_count", direction: -1 });
    }

    filteredValues.sort((p1, p2) => {
      if (
        typeof p1[sorting.column] === "string" &&
        typeof p2[sorting.column] === "string"
      ) {
        return (
          p1[sorting.column].localeCompare(p2[sorting.column]) *
          sorting.direction
        );
      } else {
        if (p1[sorting.column] < p2[sorting.column]) {
          return -1 * sorting.direction;
        } else if (p1[sorting.column] > p2[sorting.column]) {
          return 1 * sorting.direction;
        } else {
          return 0;
        }
      }
    });

    setData((d) => {
      return { ...d, filteredValues: filteredValues };
    });
  }, [searchTerm, sorting, data.values]);

  if (!activeFeature) {
    return (
      <div style={{ position: "relative", top: 0, left: 0 }}>
        <div
          style={{ position: "absolute", left: "0%", right: "0%", top: "0%" }}
        >
          <ActivateBrowserDefender />
        </div>
      </div>
    );
  }

  if (error) return <div>{t("An error occurred")}</div>;

  return (
    <Box>
      <StickyHeader>
        <Box
          display="flex"
          alignItems="center"
          height="100%" // ou fixe la hauteur selon la hauteur attendue du header
        >
          <Typography
            variant="h2"
            style={{
              marginRight: "2rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t("Extension governance")}
          </Typography>
        </Box>
      </StickyHeader>

      <Box style={{ height: "86vh" }}>
        <DataPerCompany
          content={data.filteredValues}
          onExtensionSelect={setSelectedExtension}
          selectedExtension={selectedExtension}
          onAllowExtension={handleAllow}
          onBlockExtension={handleBlock}
          onSearchTermChange={onSearchTermChange}
          loading={data.loading || actionLoading}
          updateSorting={(column, direction) => {
            setSorting({ column: column, direction: direction });
          }}
          sortingColumn={sorting.column}
          sortingDirection={sorting.direction}
        />
      </Box>
    </Box>
  );
}
